import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '../../axiosClient';
import { InteractionAudit } from '../../interfaces/interactionAudit';
import { Typography, Button, Box, CircularProgress } from '@mui/material';
import InteractionAuditDetails from './interactionAuditDetails';
import FilterListIcon from '@mui/icons-material/FilterAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import getInteractionAuditColumnDef from '../../colDef/interactionAuditColumnDef';
import SearchResultGrid from '../searchResultGrid/searchResultGrid';
import { GridPaginationModel } from '@mui/x-data-grid';
import { apiCallWrapper, createQueryParamV2, currentPoolMonth, debounce, decodedJWT, extractInteractionLevel, getOwnerType } from '../../utils/commonUtils';
import FilterDrawer from '../dashboard/filterDrawer';
import Filter from '../../interfaces/filters';
import { createFilters, getSelectedFiltersCount } from '../../utils/filterUtils';
import { processFilterToParams } from '../../utils/commonUtils';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import RoleManagement from '../../security/roleManagement';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import MonthYearSelector, { MonthYearSelectorPlaceholder } from '../baseComponents/monthYearSelector';
import { INTERACTION_AUDIT_STATUSES, RANGE, DROPDOWN } from '../../constants/constants';

const AUDIT_ATTRIBUTE_KEYS = ['Distance', 'IsBlur'] as const;

interface FilterableAttribute {
    id: number;
    field_key: string;
    value_type: string; // 'float', 'boolean', 'string', etc.
    display_name: string;
}

const baseAgencyFilters = [
    'client_reference_id',
    'client_name',
    'dpd_bucket',
    'interaction_audit_status',
    'interaction_date',
    'interaction_mode',
    'interaction_type',
    'pin_code',
    'interaction_by',
    'Distance',
    'IsBlur'
];
const baseClientFilters = [
    'client_reference_id',
    'agency_name',
    'dpd_bucket',
    'interaction_audit_status',
    'interaction_date',
    'interaction_mode',
    'interaction_type',
    'pin_code',
    'interaction_by',
    'Distance',
    'IsBlur'
];

// Helper functions for processing filter values
const processRangeFilterValue = (filterValue: unknown): number[] | null => {
    if (typeof filterValue !== 'object' || filterValue === null) {
        return null;
    }

    const rangeValue = filterValue as { min?: string | number; max?: string | number };
    const min = rangeValue.min != null ? String(rangeValue.min).trim() : '';
    const max = rangeValue.max != null ? String(rangeValue.max).trim() : '';

    if (!min && !max) {
        return null;
    }

    const minNum = min ? parseFloat(min) : null;
    const maxNum = max ? parseFloat(max) : null;

    if (minNum !== null && !isNaN(minNum) && maxNum !== null && !isNaN(maxNum)) {
        return [minNum, maxNum];
    }
    if (minNum !== null && !isNaN(minNum)) {
        return [minNum, null as any];
    }
    if (maxNum !== null && !isNaN(maxNum)) {
        return [null as any, maxNum];
    }

    return null;
};

const processIsBlurFilterValue = (filterValue: unknown): boolean | null => {
    let stringValue = '';
    
    if (Array.isArray(filterValue)) {
        stringValue = filterValue[0]?.toString().trim() || '';
    } else if (typeof filterValue === 'string') {
        stringValue = filterValue.trim();
    }
    
    if (!stringValue) {
        return null;
    }
    
    return stringValue === 'true';
};

const buildInteractionAuditQueryParams = (
    filters: Filter[],
    filterableAttributes: FilterableAttribute[],
    rowIndex: number,
    poolMonth: string
): Record<string, any> => {
    const params: Record<string, any> = {};

    if (rowIndex >= 0) {
        params.row_index = rowIndex;
    }
    if (poolMonth) {
        params.pool_month = poolMonth;
    }

    // Separate audit attribute filters from regular filters
    const auditAttributeFilters: Filter[] = [];
    const regularFilters: Filter[] = [];

    filters.forEach(filter => {
        if (AUDIT_ATTRIBUTE_KEYS.includes(filter.key as any)) {
            auditAttributeFilters.push(filter);
        } else {
            regularFilters.push(filter);
        }
    });

    // Process regular filters as query params
    regularFilters.forEach(filter => {
        processFilterToParams(filter, params);
    });

    // Process audit attribute filters to audit_attributes JSON format
    if (auditAttributeFilters.length === 0 || filterableAttributes.length === 0) {
        return params;
    }

    const auditAttributes: any[] = [];

    auditAttributeFilters.forEach(filter => {
        const attribute = filterableAttributes.find(attr => attr.field_key === filter.key);
        if (!attribute) {
            return;
        }

        let filterValue: any = null;

        if (filter.key === 'Distance' && filter.type === RANGE) {
            filterValue = processRangeFilterValue(filter.value);
        } else if (filter.key === 'IsBlur' && filter.type === DROPDOWN) {
            filterValue = processIsBlurFilterValue(filter.value);
        }

        if (filterValue !== null) {
            auditAttributes.push({
                attribute_id: attribute.id,
                value_type: attribute.value_type,
                operator: attribute.value_type === 'float' || attribute.value_type === 'integer' ? 'between' : 'eq',
                value: filterValue
            });
        }
    });

    if (auditAttributes.length > 0) {
        params.audit_attributes = JSON.stringify(auditAttributes);
    }

    return params;
};

const InteractionAuditTable = () => {
    const token: false | JWTTokenPayload = decodedJWT();
    const ownerType = getOwnerType(token);
    const clients = useSelector((state: RootState) => state.clients.clients);
    const agencies = useSelector((state: RootState) => state.agencies.agencies);
    const picklist = useSelector((state: RootState) => state.picklists.picklists);
    const users = useSelector((state: RootState) => state.userList.users);

    const [interactionsAuditList, setInteractionsAuditList] = useState<InteractionAudit[]>([]);
    const [selectedRow, setSelectedRow] = useState<InteractionAudit | null>(null);
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
    const [rowCount, setRowCount] = useState<number>(0);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [filtersCount, setFiltersCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [poolMonth, setPoolMonth] = useState<string>(currentPoolMonth());
    const [filterableAttributes, setFilterableAttributes] = useState<FilterableAttribute[]>([]);
    const [leftMargin, setLeftMargin] = useState<string>("16px");

    const fetchFilterableAttributes = async () => {
        // Only fetch if we don't already have the data
        if (filterableAttributes.length > 0) {
            return;
        }
        
        try {
            const response = await apiClient.get('/api/v1/interaction_audit/attribute_definitions');
            if (response.status === 200) {
                setFilterableAttributes(response.data as FilterableAttribute[]);
            }
        } catch (error: any) {
            console.error('Error fetching filterable attributes:', error);
        }
    };

    // Memoize the base filters selection to avoid calling RoleManagement.isAgency in dependency array
    const isAgency = useMemo(() => RoleManagement.isAgency(ownerType), [ownerType]);
    const baseFilters = useMemo(() => isAgency ? baseAgencyFilters : baseClientFilters, [isAgency]);

    const setDefaultFilters = useCallback(() => {
        // Create filters (includes Distance and IsBlur hardcoded)
        const allFilters = createFilters(
            baseFilters,
            [],
            clients,
            agencies,
            picklist,
            [],
            [],
            users,
            [],
            true
        );
        
        setFilters(allFilters);
    }, [baseFilters, clients, agencies, picklist, users]);

    useEffect(() => {
        fetchFilterableAttributes();
    }, []); // guard inside function prevents duplicate calls if data already exists

    useEffect(() => {
        setDefaultFilters();
    }, [setDefaultFilters]);

    useEffect(() => {
        setFiltersCount(getSelectedFiltersCount(filters));
        setPaginationModel({ page: 0, pageSize: 100 });
    }, [filters]);

    const fetchInteractions = useCallback(async () => {
        try {
            setLoading(true);
            const queryParams = buildInteractionAuditQueryParams(filters, filterableAttributes, paginationModel.page, poolMonth);
            const queryString = createQueryParamV2(queryParams);
            const response = await apiCallWrapper(() => apiClient.get(`/api/v1/interaction_audit?${queryString}`));
            const dataWithStatus = response.data.interaction_audit_with_details.map((item: InteractionAudit) => {
                if (!item.agency_name) {
                    item.agency_name = 'Unassigned';
                }
                // split the dash‑separated audit strings and grab the 0th element
                const l0_audit_by_agency = extractInteractionLevel(item.agency_audit_value || '', 0, 1);
                const l0_audit_by_client = extractInteractionLevel(item.client_audit_value || '', 0, 1);
                const statusValue = RoleManagement.isAgency(ownerType) ? item.agency_audit_value : item.client_audit_value;
                const status = statusValue ? INTERACTION_AUDIT_STATUSES.completed : INTERACTION_AUDIT_STATUSES.pending;

                return {
                    ...item,
                    status,
                    l0_audit_by_agency,
                    l0_audit_by_client,
                };
            });
            setRowCount(response.data.rows_count);
            setInteractionsAuditList(dataWithStatus);
        } catch (error: any) {
            alert(error.response?.data?.message || error.message || "An error occurred.");
        } finally {
            setLoading(false);
        }
    }, [filters, filterableAttributes, paginationModel, poolMonth]);

    const toggleFilterDrawer = () => {
        setIsFilterOpen(!isFilterOpen);
        if (!isFilterOpen) {
            setLeftMargin("166px");
        } else {
            setLeftMargin("16px");
        }
    };

    const debouncedFunc = useMemo(() => debounce(fetchInteractions, 700), [fetchInteractions]);

    useEffect(() => {
        debouncedFunc.cancel();
        debouncedFunc();
        return () => {
            debouncedFunc.cancel();
        };
    }, [filters, filterableAttributes, paginationModel, poolMonth, debouncedFunc]);

    const handleAuditClick = (row: InteractionAudit) => {
        const index = interactionsAuditList.findIndex(item => item.interaction_audit_id === row.interaction_audit_id);
        setCurrentIndex(index);
        setSelectedRow(row);
    };

    const columns = getInteractionAuditColumnDef(handleAuditClick);

    const handleClose = () => {
        setSelectedRow(null);
    };

    const handleNext = () => {
        if (currentIndex !== null && currentIndex < interactionsAuditList.length - 1) {
            const newIndex = currentIndex + 1;
            const nextRow = interactionsAuditList[newIndex];
            setCurrentIndex(newIndex);
            setSelectedRow(nextRow);
        }
    };

    const handlePrevious = () => {
        if (currentIndex !== null && currentIndex > 0) {
            const newIndex = currentIndex - 1;
            const prevRow = interactionsAuditList[newIndex];
            setCurrentIndex(newIndex);
            setSelectedRow(prevRow);
        }
    };

    const resetFilters = () => {
        setDefaultFilters();
        setPaginationModel({ page: 0, pageSize: 100 });
    };

    return (
        <Box sx={{ height: '90vh', ml: leftMargin, mr: 2, mt: 2 }}>
            <FilterDrawer
                filters={filters}
                setFilters={setFilters}
                drawerOpen={isFilterOpen}
                toggleDrawer={toggleFilterDrawer}
            />
            {selectedRow ? (
                <InteractionAuditDetails
                    row={selectedRow}
                    onClose={handleClose}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    hasNext={currentIndex !== null && currentIndex < interactionsAuditList.length - 1}
                    hasPrevious={currentIndex !== null && currentIndex > 0}
                />
            ) : (
                <>
                    <Typography variant="h5" gutterBottom>Interaction Audit</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            variant={filtersCount === 0 ? "outlined" : "contained"}
                            color="primary"
                            startIcon={<FilterListIcon fontSize="small" />}
                            onClick={toggleFilterDrawer}
                            aria-label="open drawer"
                        >
                            <Typography variant="subtitle2">Filters [{filtersCount}]</Typography>
                        </Button>
                        <Button style={{ marginLeft: "10px", marginRight: "10px" }}
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon fontSize="small" />}
                            onClick={resetFilters}
                            aria-label="Refresh"
                        >
                            <Typography variant="subtitle2">Reset</Typography>
                        </Button>
                        {isAgency ? (
                            <MonthYearSelectorPlaceholder />
                        ) : (
                            <MonthYearSelector
                                keys={poolMonth}
                                onDateSelection={setPoolMonth}
                            />
                        )}
                    </Box>
                    {loading ? (
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress color="primary" />
                        </Box>
                    ) : (
                        <Box mt={2} sx={{ height: '72vh', display: 'flex', flexDirection: 'column' }}>
                            <SearchResultGrid
                                disableFilter={true}
                                disableSorting={true}
                                disableAsecDescSorting={true}
                                getRowId={(row) => row.interaction_audit_id}
                                results={interactionsAuditList}
                                onSelectRow={() => {}}
                                columns={columns}
                                rowCount={rowCount}
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                            />   
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default InteractionAuditTable;
